import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Tooltip,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import GoogleMapReact from "google-map-react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  getCompleteWorkDiaryAction,
  getJobOptimiseAction,
} from "../../../../store/reducers/workDiarySlice";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

// Replace with your actual Mapbox access token
const MAPBOX_TOKEN =
  "pk.eyJ1IjoiYWJoaW1hbnl1LWt1c2h3YWhhIiwiYSI6ImNtbTJ4N3RmdDBhYTYyb3NkeG93eDNleDYifQ._adeHvAsIHMyKt1qNw4O0g";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

// ---------- Color mapping for job types ----------
const JOB_TYPE_COLORS: Record<string, string> = {
  "Part Load": "#E53935", // Red
  "Box Delivery": "#43A047", // Green
  "Full Move": "#1E88E5", // Blue
  Storage: "#7B1FA2", // Purple
  Packing: "#FF9800", // Orange
  Removal: "#009688", // Teal
  Delivery: "#00BCD4", // Cyan
  Survey: "#795548", // Brown
};

const getJobColor = (jobType: string): string => {
  return JOB_TYPE_COLORS[jobType] || "#086a56";
};

// ---------- Interfaces ----------
interface RouteJob {
  id: string;
  job: string;
  task: string;
  volume: string;
  time: string;
  distance: string;
  lat: number;
  lng: number;
  color: string;
  customerName: string;
  jobType: string;
  vehicle: string;
  driver: string;
  status?: string;
  allocatedLoad?: number;
}

interface RouteOptimiseModalProps {
  open: boolean;
  onClose: () => void;
  filteredData?: any;
}

interface GoogleMapPinProps {
  lat: number;
  lng: number;
  job: RouteJob;
  onClick: () => void;
}

const GoogleMapPin: React.FC<GoogleMapPinProps> = ({ job, onClick }) => {
  return (
    <Tooltip
      title={
        <Box>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
            {job.customerName} / {job.id}
          </Typography>
          <Typography sx={{ fontSize: "11px" }}>{job.jobType}</Typography>
          <Typography sx={{ fontSize: "11px" }}>{job.volume}</Typography>
          <Typography sx={{ fontSize: "11px" }}>{job.time}</Typography>
          <Typography sx={{ fontSize: "11px", color: "#90caf9" }}>
            {job.vehicle ? `${job.vehicle} - ${job.driver}` : "No vehicle assigned"}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box
        onClick={onClick}
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          backgroundColor: getJobColor(job.jobType),
          border: "2px solid #fff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
          cursor: "pointer",
          transform: "translate(-50%, -50%)",
          transition: "transform 0.15s",
          "&:hover": { transform: "translate(-50%, -50%) scale(1.4)" },
        }}
      />
    </Tooltip>
  );
};

// ---------- Utility functions ----------

/** Haversine distance in miles between two lat/lng points */
const haversineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/** Parse numeric volume from string like "150 cuft" */
const parseVolume = (vol: string): number => {
  return parseInt(vol) || 0;
};

/** Estimate travel duration (rough: 30mph average) */
const estimateDuration = (miles: number): string => {
  const hours = miles / 30;
  if (hours < 1) return `${Math.round(hours * 60)} mins`;
  return `${hours.toFixed(1)} hrs`;
};

/** Format seconds into human-readable duration */
const formatDuration = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  if (hrs === 0) return `${mins} mins`;
  if (mins === 0) return `${hrs} hrs`;
  return `${hrs} hrs ${mins} mins`;
};

/** Fetch actual driving route from Mapbox Directions API */
const fetchDrivingRoute = async (
  jobs: RouteJob[],
  token: string,
): Promise<{
  geometry: GeoJSON.LineString | null;
  distance: number; // meters
  duration: number; // seconds
  legDistances: number[]; // meters per leg
  legDurations: number[]; // seconds per leg
} | null> => {
  if (jobs.length < 2) return null;

  // Mapbox Directions API accepts max 25 waypoints
  const waypoints = jobs
    .slice(0, 25)
    .map((j) => `${j.lng},${j.lat}`)
    .join(";");

  const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${waypoints}?geometries=geojson&overview=full&steps=false&access_token=${token}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.routes || data.routes.length === 0) return null;

    const route = data.routes[0];
    return {
      geometry: route.geometry,
      distance: route.distance, // meters
      duration: route.duration, // seconds
      legDistances: route.legs.map((l: any) => l.distance),
      legDurations: route.legs.map((l: any) => l.duration),
    };
  } catch {
    return null;
  }
};

/** Check if two time windows overlap / conflict */
const hasTimeConflict = (time1: string, time2: string): boolean => {
  const parse = (t: string) => {
    const parts = t.split("-");
    if (parts.length !== 2) return null;
    const toMin = (s: string) => {
      const [h, m] = s.replace(/\s/g, "").split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    return { start: toMin(parts[0]), end: toMin(parts[1]) };
  };
  const r1 = parse(time1);
  const r2 = parse(time2);
  if (!r1 || !r2) return false;
  return r1.start < r2.end && r2.start < r1.end;
};

/** Simple nearest-neighbour route ordering */
const optimizeRouteOrder = (jobs: RouteJob[]): RouteJob[] => {
  if (jobs.length <= 1) return [...jobs];
  const ordered: RouteJob[] = [jobs[0]];
  const remaining = jobs.slice(1);
  while (remaining.length > 0) {
    const last = ordered[ordered.length - 1];
    let nearestIdx = 0;
    let nearestDist = Infinity;
    remaining.forEach((j, i) => {
      const d = haversineDistance(last.lat, last.lng, j.lat, j.lng);
      if (d < nearestDist) {
        nearestDist = d;
        nearestIdx = i;
      }
    });
    ordered.push(remaining.splice(nearestIdx, 1)[0]);
  }
  return ordered;
};

/** Calculate total route distance for ordered jobs */
const calcTotalRouteDistance = (jobs: RouteJob[]): number => {
  let total = 0;
  for (let i = 1; i < jobs.length; i++) {
    total += haversineDistance(
      jobs[i - 1].lat,
      jobs[i - 1].lng,
      jobs[i].lat,
      jobs[i].lng,
    );
  }
  return total;
};

/** Convert an API optimisedRouteStop item to a RouteJob */
const mapApiStopToRouteJob = (stop: any, idx: number, allStops: any[]): RouteJob => {
  const startDt = stop.start_date_time || stop.start;
  const endDt = stop.end_date_time || stop.end;
  const timeStr = startDt && endDt
    ? `${dayjs(startDt).format("HH:mm")}-${dayjs(endDt).format("HH:mm")}`
    : "";

  // Volume: prefer jobVolume, else volumeM3 converted to cuft (1 m³ ≈ 35.3147 cuft)
  const volM3 = stop.volumeM3 ?? stop.jobVolume ?? 0;
  const volCuft = Math.round(volM3 * 35.3147);

  // Distance from previous stop
  let dist = "0 mi";
  if (idx > 0) {
    const prev = allStops[idx - 1];
    const prevLat = prev.lat ?? prev.collection_latitude;
    const prevLng = prev.lng ?? prev.collection_longitude;
    const curLat = stop.lat ?? stop.collection_latitude;
    const curLng = stop.lng ?? stop.collection_longitude;
    if (prevLat && prevLng && curLat && curLng) {
      dist = `${haversineDistance(prevLat, prevLng, curLat, curLng).toFixed(1)} mi`;
    }
  }

  return {
    id: stop.job_id || stop.lead_id?.toString() || `stop-${idx}`,
    job: `${stop.customer_name || "Unknown"} Ref ID: ${stop.lead_id || ""}`,
    task: stop.job_title || stop.job_type || "",
    volume: `${volCuft} cuft`,
    time: timeStr,
    distance: dist,
    lat: stop.lat ?? stop.collection_latitude ?? 0,
    lng: stop.lng ?? stop.collection_longitude ?? 0,
    color: getJobColor(stop.job_type || ""),
    customerName: stop.customer_name || "Unknown",
    jobType: stop.job_type || "",
    vehicle: stop.vehicle_type || "",
    driver: "",
    status: stop.status || "",
    allocatedLoad: volCuft,
  };
};


// =============================================
// COMPONENT
// =============================================
const RouteGoogleOptimiseModal: React.FC<RouteOptimiseModalProps> = ({
  open,
  onClose,
  filteredData,
}) => {
  const { workDiaryTypeOptions, completeWorkDiaryData, jobOptimiseData } = useSelector(
    (state: any) => state?.workDiary,
  );

   const { userProfile } = useSelector((state: any) => state?.user);;

  const dispatch = useDispatch();
  // ---------- Filter state (kept as-is) ----------
  const [activeTab, setActiveTab] = useState<"suggested" | "custom">(
    "suggested",
  );
  const [filterData, setFilterData] = useState({
    startDate: filteredData?.startDateRange ?? dayjs().format("YYYY-MM-DD"),
    endDate: filteredData?.endDateRange ?? dayjs().format("YYYY-MM-DD"),
    jobTypes: [] as string[],
    jobIds: "",
    radius: "15",
    vanCapacity: "400",
  });
  const [allocatedLoad, setAllocatedLoad] = useState("");

  useEffect(() => {
    if (open) {
      dispatch(
        getCompleteWorkDiaryAction({
          search: "",
          // actionType: "professional",
          startDateRange: filterData.startDate,
          endDateRange: filterData.endDate,
        }),
      );
    }
  }, [dispatch, filterData, open]);

  // ---------- Map / popup state ----------
  const [selectedPopup, setSelectedPopup] = useState<RouteJob | null>(null);
  const [viewState, setViewState] = useState({
    latitude: 51.52,
    longitude: -0.08,
    zoom: 12,
  });
  const [googleMapInstance, setGoogleMapInstance] = useState<any>(null);
  const [googleMapsApi, setGoogleMapsApi] = useState<any>(null);
  const routePolylineRef = useRef<any>(null);

  // Center map on API data when it arrives
  useEffect(() => {
    const stops = jobOptimiseData?.optimisedRouteStops;
    if (stops && stops.length > 0) {
      const firstLat = stops[0].lat ?? stops[0].collection_latitude;
      const firstLng = stops[0].lng ?? stops[0].collection_longitude;
      if (firstLat && firstLng) {
        setViewState((prev) => ({
          ...prev,
          latitude: firstLat,
          longitude: firstLng,
          zoom: stops.length === 1 ? 13 : 10,
        }));
      }
    }
  }, [jobOptimiseData]);

  // ---------- Suggestion / custom route state ----------
  const [suggestedJobs, setSuggestedJobs] = useState<RouteJob[]>([]);
  const [customSelectedIds, setCustomSelectedIds] = useState<string[]>([]);
  const [hasSuggested, setHasSuggested] = useState(false);

  // ---------- Driving route from Mapbox Directions API ----------
  const [, setDrivingRouteGeo] = useState<GeoJSON.LineString | null>(null);
  const [drivingDistance, setDrivingDistance] = useState<number | null>(null); // in meters
  const [drivingDuration, setDrivingDuration] = useState<number | null>(null); // in seconds
  const [routeLoading, setRouteLoading] = useState(false);

  // ---------- Map API optimisedRouteStops into RouteJob[] ----------
  const apiRouteJobs: RouteJob[] = useMemo(() => {
    const stops = jobOptimiseData?.optimisedRouteStops;
    if (!stops || !Array.isArray(stops) || stops.length === 0) return [];
    return stops.map((s: any, i: number) => mapApiStopToRouteJob(s, i, stops));
  }, [jobOptimiseData]);

  // ---------- All diary jobs from completeWorkDiaryData ----------
  const allDiaryJobs: RouteJob[] = useMemo(() => {
    // if (!completeWorkDiaryData?.jobList || !Array.isArray(completeWorkDiaryData?.jobList)) return [];
    if (!completeWorkDiaryData || !Array.isArray(completeWorkDiaryData)) return [];
    const jobs: RouteJob[] = [];
    // completeWorkDiaryData?.jobList?.forEach((item: any) => {
    completeWorkDiaryData?.forEach((item: any) => {
      
      const tasks = item.tasks || [];
      tasks.forEach((t: any, idx: number) => {
        if (t.status === "Cancelled" || t.status === "Completed") return;
        const lat = t.collection_latitude ?? t.lat ?? 0;
        const lng = t.collection_longitude ?? t.lng ?? 0;
        if (!lat && !lng) return;
        const startDt = t.start_date_time || t.start;
        const endDt = t.end_date_time || t.end;
        const timeStr = startDt && endDt
          ? `${dayjs(startDt).format("HH:mm")}-${dayjs(endDt).format("HH:mm")}`
          : "";
        const volM3 = t.volumeM3 ?? t.jobVolume ?? 0;
        const volCuft = Math.round(volM3 * 35.3147);
        jobs.push({
          id: t.job_id || t.lead_id?.toString() || `diary-${idx}`,
          job: `${t.customer_name || "Unknown"} Ref ID: ${t.lead_id || ""}`,
          task: t.job_title || t.job_type || "",
          volume: `${volCuft} cuft`,
          time: timeStr,
          distance: "0 mi",
          lat,
          lng,
          color: getJobColor(t.job_type || ""),
          customerName: t.customer_name || "Unknown",
          jobType: t.job_type || "",
          vehicle: t.vehicle_type || "",
          driver: "",
          status: t.status || "",
          allocatedLoad: volCuft,
        });
      });
    });
    return jobs;
  }, [completeWorkDiaryData]);

  // ---------- Filtered jobs for custom tab (exclude cancelled/completed, apply job type filter) ----------
  const filteredJobs = useMemo(() => {
    const source =
      allDiaryJobs.length > 0
        ? allDiaryJobs
        : apiRouteJobs.length > 0
          ? apiRouteJobs
          : [];
    return source.filter((job) => {
      if (job.status === "Cancelled" || job.status === "Completed")
        return false;
      if (
        filterData.jobTypes.length > 0 &&
        !filterData.jobTypes.includes(job.jobType)
      )
        return false;
      return true;
    });
  }, [filterData.jobTypes, allDiaryJobs, apiRouteJobs]);

  useEffect(() => {
    setCustomSelectedIds((prev) =>
      prev.filter((id) => filteredJobs.some((job) => job.id === id)),
    );
  }, [filteredJobs]);

  // ---------- Active route jobs (suggested or custom-selected) ----------
  const activeRouteJobs = useMemo(() => {
    if (activeTab === "suggested") {
      // Priority: API optimised route stops > suggested jobs > filtered jobs
      if (apiRouteJobs.length > 0) return apiRouteJobs;
      const jobs = suggestedJobs.length > 0 ? suggestedJobs : filteredJobs;
      if (jobs.length === 0) return [];
      const ordered =
        suggestedJobs.length > 0 ? jobs : optimizeRouteOrder(jobs);
      return ordered.map((j, idx) => ({
        ...j,
        distance:
          idx === 0
            ? "0 mi"
            : `${haversineDistance(ordered[idx - 1].lat, ordered[idx - 1].lng, j.lat, j.lng).toFixed(1)} mi`,
      }));
    }
    // custom mode – optimise selected jobs
    const selected = filteredJobs.filter((j) =>
      customSelectedIds.includes(j.id),
    );
    if (selected.length === 0) return [];
    const ordered = optimizeRouteOrder(selected);
    // assign inter-stop distances
    return ordered.map((j, idx) => ({
      ...j,
      distance:
        idx === 0
          ? "0 mi"
          : `${haversineDistance(ordered[idx - 1].lat, ordered[idx - 1].lng, j.lat, j.lng).toFixed(1)} mi`,
    }));
  }, [activeTab, apiRouteJobs, suggestedJobs, filteredJobs, customSelectedIds]);

  // ---------- Fetch driving route when active jobs change ----------
  useEffect(() => {
    let cancelled = false;

    if (activeRouteJobs.length < 2) {
      setDrivingRouteGeo(null);
      setDrivingDistance(null);
      setDrivingDuration(null);
      return;
    }

    setRouteLoading(true);
    fetchDrivingRoute(activeRouteJobs, MAPBOX_TOKEN).then((result) => {
      if (cancelled) return;
      setRouteLoading(false);
      if (result) {
        setDrivingRouteGeo(result.geometry);
        setDrivingDistance(result.distance);
        setDrivingDuration(result.duration);
      } else {
        // Fallback: clear driving route, will use straight lines
        setDrivingRouteGeo(null);
        setDrivingDistance(null);
        setDrivingDuration(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeRouteJobs]);

  // ---------- Route calculations (prefer API routeSummary when available) ----------
  const routeSummary = jobOptimiseData?.routeSummary;

  const totalVolume = useMemo(() => {
    if (routeSummary && activeTab === "suggested") {
      // API returns m³ – convert to cuft
      return Math.round((routeSummary.totalVolumeM3 ?? 0) * 35.3147);
    }
    return activeRouteJobs.reduce(
      (acc, job) => acc + parseVolume(job.volume),
      0,
    );
  }, [activeRouteJobs, routeSummary, activeTab]);

  // Use API summary > Mapbox driving distance > Haversine fallback
  const totalMiles = useMemo(() => {
    if (routeSummary && activeTab === "suggested" && routeSummary.totalMiles != null) {
      return routeSummary.totalMiles.toFixed(1);
    }
    if (drivingDistance !== null) {
      return (drivingDistance / 1609.344).toFixed(1); // meters → miles
    }
    const d = calcTotalRouteDistance(activeRouteJobs);
    return d.toFixed(1);
  }, [activeRouteJobs, drivingDistance, routeSummary, activeTab]);

  // Use API summary > Mapbox driving duration > estimate
  const estDuration = useMemo(() => {
    if (routeSummary && activeTab === "suggested" && routeSummary.totalDurationMinutes != null) {
      const mins = routeSummary.totalDurationMinutes;
      if (mins < 60) return `${Math.round(mins)} mins`;
      return `${(mins / 60).toFixed(1)} hrs`;
    }
    if (drivingDuration !== null) {
      return formatDuration(drivingDuration);
    }
    return estimateDuration(parseFloat(totalMiles));
  }, [totalMiles, drivingDuration, routeSummary, activeTab]);

  // Capacity from API criteria
  const apiCapacityCuft = useMemo(() => {
    if (jobOptimiseData?.criteria?.vanCapacityFt3) {
      return parseInt(jobOptimiseData.criteria.vanCapacityFt3);
    }
    return parseInt(filterData.vanCapacity) || 400;
  }, [jobOptimiseData, filterData.vanCapacity]);

  // ---------- Warnings ----------
  const currentWarnings = useMemo(() => {
    const w: string[] = [];
    if (activeRouteJobs.length === 0) return w;

    const cap = apiCapacityCuft;
    if (totalVolume > cap) {
      w.push(
        `Capacity exceeded! Total volume (${totalVolume} cuft) exceeds van capacity (${cap} cuft).`,
      );
    }
    // Also check API-reported capacity breach
    if (routeSummary?.capacityBreached) {
      w.push("Capacity breached as reported by the route optimiser.");
    }
    // time conflict check
    for (let i = 0; i < activeRouteJobs.length; i++) {
      for (let j = i + 1; j < activeRouteJobs.length; j++) {
        if (hasTimeConflict(activeRouteJobs[i].time, activeRouteJobs[j].time)) {
          w.push(
            `Time conflict: "${activeRouteJobs[i].customerName}" (${activeRouteJobs[i].time}) overlaps with "${activeRouteJobs[j].customerName}" (${activeRouteJobs[j].time}).`,
          );
        }
      }
    }
    const maxDist = parseFloat(filterData.radius) || 15;
    if (parseFloat(totalMiles) > maxDist) {
      w.push(
        `Route distance (${totalMiles} mi) exceeds max distance (${maxDist} mi).`,
      );
    }
    return w;
  }, [
    activeRouteJobs,
    apiCapacityCuft,
    filterData.radius,
    totalVolume,
    totalMiles,
    routeSummary,
  ]);

  useEffect(() => {
    if (!googleMapInstance || !googleMapsApi) return;

    if (routePolylineRef.current) {
      routePolylineRef.current.setMap(null);
      routePolylineRef.current = null;
    }

    if (activeRouteJobs.length < 2) return;

    const jobsForRoute = activeRouteJobs.slice(0, 25);
    const origin = {
      lat: jobsForRoute[0].lat,
      lng: jobsForRoute[0].lng,
    };
    const destination = {
      lat: jobsForRoute[jobsForRoute.length - 1].lat,
      lng: jobsForRoute[jobsForRoute.length - 1].lng,
    };
    const waypoints = jobsForRoute.slice(1, -1).map((job) => ({
      location: { lat: job.lat, lng: job.lng },
      stopover: true,
    }));

    setRouteLoading(true);
    const directionsService = new googleMapsApi.DirectionsService();
    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false,
        travelMode: googleMapsApi.TravelMode.DRIVING,
      },
      (result: any, status: any) => {
        setRouteLoading(false);

        if (status === googleMapsApi.DirectionsStatus.OK && result?.routes?.[0]) {
          const overviewPath = result.routes[0].overview_path || [];
          const path = overviewPath.map((point: any) => ({
            lat: point.lat(),
            lng: point.lng(),
          }));

          routePolylineRef.current = new googleMapsApi.Polyline({
            path,
            geodesic: true,
            strokeColor: "#009688",
            strokeOpacity: 0.9,
            strokeWeight: 4,
          });
          routePolylineRef.current.setMap(googleMapInstance);
          return;
        }

        // Fallback to straight line if Directions API fails
        const fallbackPath = jobsForRoute.map((job) => ({
          lat: job.lat,
          lng: job.lng,
        }));
        routePolylineRef.current = new googleMapsApi.Polyline({
          path: fallbackPath,
          geodesic: true,
          strokeColor: "#90a4ae",
          strokeOpacity: 0.85,
          strokeWeight: 4,
        });
        routePolylineRef.current.setMap(googleMapInstance);
      },
    );

    return () => {
      if (routePolylineRef.current) {
        routePolylineRef.current.setMap(null);
        routePolylineRef.current = null;
      }
    };
  }, [googleMapInstance, googleMapsApi, activeRouteJobs]);

  // ---------- Handlers ----------
  const handleJobTypeChange = (type: string) => {
    setFilterData((prev) => {
      const jt = prev.jobTypes.includes(type)
        ? prev.jobTypes.filter((t) => t !== type)
        : [...prev.jobTypes, type];
      return { ...prev, jobTypes: jt };
    });
  };

  const handleSuggestCombinations = useCallback(() => {
    const referenceId = filterData.jobIds.trim();
    const maxRadius = parseFloat(filterData.radius) || 15;
    const cap = parseInt(filterData.vanCapacity) || 400;

    // Find reference job
    let refJob = filteredJobs.find(
      (j) =>
        j.id === referenceId ||
        j.job.toLowerCase().includes(referenceId.toLowerCase()),
    );
    if (!refJob && filteredJobs.length > 0) {
      refJob = filteredJobs[0]; // fallback to first job
    }
    if (!refJob) {
      setSuggestedJobs([]);
      setHasSuggested(true);
      return;
    }

    // Filter by distance from reference
    const nearby = filteredJobs.filter((j) => {
      if (j.id === refJob!.id) return true;
      const dist = haversineDistance(refJob!.lat, refJob!.lng, j.lat, j.lng);
      return dist <= maxRadius;
    });

    // Greedily add jobs up to capacity
    let accumulated = 0;
    const group: RouteJob[] = [];
    // reference first
    accumulated += parseVolume(refJob.volume);
    group.push(refJob);

    const rest = nearby.filter((j) => j.id !== refJob!.id);
    for (const j of rest) {
      const vol = parseVolume(j.volume);
      if (accumulated + vol <= cap) {
        accumulated += vol;
        group.push(j);
      }
    }

    // Optimise order (nearest-neighbour)
    const optimized = optimizeRouteOrder(group);

    // Assign inter-stop distances
    const withDistances = optimized.map((j, idx) => ({
      ...j,
      color: getJobColor(j.jobType),
      distance:
        idx === 0
          ? "0 mi"
          : `${haversineDistance(optimized[idx - 1].lat, optimized[idx - 1].lng, j.lat, j.lng).toFixed(1)} mi`,
    }));

    setSuggestedJobs(withDistances);
    setHasSuggested(true);
  }, [filterData, filteredJobs]);

  const handleCustomToggle = (jobId: string) => {
    setCustomSelectedIds((prev) =>
      prev.includes(jobId)
        ? prev.filter((id) => id !== jobId)
        : [...prev, jobId],
    );
  };

  // Jobs to show on map
  const mapJobs = useMemo(() => {
    if (activeRouteJobs.length > 0) return activeRouteJobs;
    return filteredJobs;
  }, [activeRouteJobs, filteredJobs]);

  // Build legend from visible job types
  const legendTypes = useMemo(() => {
    const types = new Set(mapJobs.map((j) => j.jobType));
    return Array.from(types).map((t) => ({ type: t, color: getJobColor(t) }));
  }, [mapJobs]);

  useEffect(() => {
    if (open) {
      dispatch(getJobOptimiseAction(filterData));
    }
  }, [dispatch, filterData, open]);

  useEffect(() => {});

  // const allTasks = completeWorkDiaryData?.jobList?.flatMap(
  //   (item: any) => item.tasks || [],
  // );

const allTasks = completeWorkDiaryData?.flatMap(
    (item: any) => item.tasks || [],
  );

  // Date change handlers
  const handleStartDateChange = (newValue: any) => {
    if (!newValue) return;
    setFilterData({
      ...filterData,
      startDate: newValue.format("YYYY-MM-DD"),
    });
  };

  const handleEndDateChange = (newValue: any) => {
    if (!newValue) return;
    setFilterData({
      ...filterData,
      endDate: newValue.format("YYYY-MM-DD"),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "100vw",
          maxWidth: "1400px",
          height: "95vh",
          maxHeight: "95vh",
          borderRadius: "8px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          py: 1.5,
          px: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontFamily: "Eurostile", color: "#201F1E" }}
        >
          Route Optimise
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        {/* Tabs */}
        <Box sx={{ display: "flex", gap: 1, px: 3, pt: 2, pb: 1 }}>
          <Button
            variant={activeTab === "suggested" ? "contained" : "outlined"}
            size="small"
            onClick={() => setActiveTab("suggested")}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              backgroundColor:
                activeTab === "suggested" ? "#009688" : "transparent",
              color: activeTab === "suggested" ? "#fff" : "#009688",
              borderColor: "#009688",
              fontWeight: 600,
              fontSize: "13px",
              px: 2,
              "&:hover": {
                backgroundColor:
                  activeTab === "suggested"
                    ? "#00796b"
                    : "rgba(0,150,136,0.08)",
                borderColor: "#009688",
              },
            }}
          >
            Suggested Route
          </Button>
          <Button
            variant={activeTab === "custom" ? "contained" : "outlined"}
            size="small"
            onClick={() => setActiveTab("custom")}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              backgroundColor:
                activeTab === "custom" ? "#009688" : "transparent",
              color: activeTab === "custom" ? "#fff" : "#009688",
              borderColor: "#009688",
              fontWeight: 600,
              fontSize: "13px",
              px: 2,
              "&:hover": {
                backgroundColor:
                  activeTab === "custom" ? "#00796b" : "rgba(0,150,136,0.08)",
                borderColor: "#009688",
              },
            }}
          >
            Create Your Own Route
          </Button>
        </Box>

        {/* Warnings */}
        {/* {currentWarnings.length > 0 && (
          <Box sx={{ px: 3, pt: 1 }}>
            {currentWarnings.map((w, i) => (
              <Alert
                key={i}
                severity="warning"
                icon={<WarningAmberIcon fontSize="small" />}
                sx={{ mb: 0.5, py: 0, fontSize: "12px" }}
              >
                {w}
              </Alert>
            ))}
          </Box>
        )} */}

        {/* Main Content */}
        <Box sx={{ display: "flex", px: 3, pb: 2, gap: 3, pt: 1 }}>
          {/* ========== Left Sidebar – Filters ========== */}
          <Box sx={{ width: "220px", minWidth: "220px", pr: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                mb: 2,
                color: "#201F1E",
                letterSpacing: "0.5px",
              }}
            >
              FILTERS
            </Typography>

       {/* Start Date - MUI X DatePicker */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              Start Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={filterData.startDate ? dayjs(filterData.startDate) : null}
                onChange={handleStartDateChange}
                format={userProfile?.dateFormat || "DD/MM/YYYY"}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      mb: 2,
                      "& .MuiInputBase-root": {
                        fontSize: "13px",
                        height: 36,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            {/* End Date - MUI X DatePicker */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              End Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={filterData.endDate ? dayjs(filterData.endDate) : null}
                onChange={handleEndDateChange}
                format={userProfile?.dateFormat || "DD/MM/YYYY"}
                minDate={filterData.startDate ? dayjs(filterData.startDate) : undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      mb: 2,
                      "& .MuiInputBase-root": {
                        fontSize: "13px",
                        height: 36,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            {/* Job Type */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              Job Type
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 1, mb: 2, borderColor: "#ccc", borderRadius: "4px" }}
            >
              <FormGroup>
                {workDiaryTypeOptions &&
                  workDiaryTypeOptions.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              filterData.jobTypes?.includes(item) || false
                            }
                            onChange={() => handleJobTypeChange(item)}
                            sx={{
                              color: "#009688",
                              "&.Mui-checked": { color: "#009688" },
                              py: 0.3,
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "13px" }}>
                            {item}
                          </Typography>
                        }
                      />
                    </React.Fragment>
                  ))}
              </FormGroup>
            </Paper>

            {/* Reference Job */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              Reference Job
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={filterData?.jobIds || ""}
              onChange={(e) =>
                setFilterData({ ...filterData, jobIds: e.target.value })
              }
              sx={{ mb: 2 }}
              InputProps={{ sx: { fontSize: "13px", height: 36 } }}
            >
              {allTasks?.map((task: any, index: number) => (
                <MenuItem key={index} value={task?.job_id}>
                  {task?.customer_name} Ref ID: {task?.lead_id}
                </MenuItem>
              ))}
            </TextField>

            {/* Max Distance */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              Max Distance (miles)
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={filterData?.radius}
              onChange={(e) =>
                setFilterData({ ...filterData, radius: e.target.value })
              }
              sx={{ mb: 2 }}
              InputProps={{ sx: { fontSize: "13px", height: 36 } }}
            />

            {/* Van Capacity */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              Van Capacity (cuft)
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={filterData?.vanCapacity}
              onChange={(e) =>
                setFilterData({ ...filterData, vanCapacity: e.target.value })
              }
              sx={{ mb: 2 }}
              InputProps={{ sx: { fontSize: "13px", height: 36 } }}
            />

            {/* Allocated Load (optional) */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}
            >
              Allocated Load (cuft)
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Optional"
              value={allocatedLoad}
              onChange={(e) => setAllocatedLoad(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{ sx: { fontSize: "13px", height: 36 } }}
            />

            {/* Suggest Combinations Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleSuggestCombinations}
              disabled={activeTab === "custom"}
              sx={{
                textTransform: "none",
                backgroundColor: "#009688",
                fontWeight: 600,
                fontSize: "14px",
                borderRadius: "6px",
                py: 1.2,
                mt: 1,
                "&:hover": { backgroundColor: "#00796b" },
                "&.Mui-disabled": { backgroundColor: "#ccc" },
              }}
            >
              Suggest Combinations
            </Button>
          </Box>

          {/* ========== Right Content – Map & Table ========== */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Map header + legend */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#201F1E" }}
              >
                Route Map
              </Typography>
              {/* Legend */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {legendTypes.map((lt) => (
                  <Box
                    key={lt.type}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <FiberManualRecordIcon
                      sx={{ fontSize: 10, color: lt.color }}
                    />
                    <Typography sx={{ fontSize: "11px", color: "#555" }}>
                      {lt.type}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Map container */}
            <Box
              sx={{
                width: "100%",
                height: 320,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
                mb: 2,
                position: "relative",
              }}
            >
              {GOOGLE_MAPS_API_KEY ? (
                <GoogleMapReact
                  bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
                  defaultCenter={{ lat: 51.52, lng: -0.08 }}
                  center={{ lat: viewState.latitude, lng: viewState.longitude }}
                  zoom={viewState.zoom}
                  onChange={({ center, zoom }: any) =>
                    setViewState((prev) => ({
                      ...prev,
                      latitude: center.lat,
                      longitude: center.lng,
                      zoom,
                    }))
                  }
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => {
                    setGoogleMapInstance(map);
                    setGoogleMapsApi(maps);
                  }}
                  options={{
                    fullscreenControl: false,
                    mapTypeControl: false,
                    streetViewControl: false,
                  }}
                >
                  {mapJobs.map((job) => (
                    <GoogleMapPin
                      key={job.id}
                      lat={job.lat}
                      lng={job.lng}
                      job={job}
                      onClick={() => setSelectedPopup(job)}
                    />
                  ))}
                </GoogleMapReact>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fafafa",
                    px: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: "#d32f2f", fontWeight: 600 }}>
                    Google map key is missing. Set REACT_APP_GOOGLE_MAPS_API_KEY and restart the app.
                  </Typography>
                </Box>
              )}

              {routeLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    backgroundColor: "rgba(255,255,255,0.9)",
                    borderRadius: "4px",
                    px: 1.5,
                    py: 0.5,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    zIndex: 10,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#009688",
                      fontWeight: 600,
                    }}
                  >
                    Loading route...
                  </Typography>
                </Box>
              )}

              {GOOGLE_MAPS_API_KEY && !routeLoading && activeRouteJobs.length < 2 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    bottom: 10,
                    backgroundColor: "rgba(255,255,255,0.95)",
                    borderRadius: "4px",
                    px: 1.5,
                    py: 0.75,
                    zIndex: 10,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography sx={{ fontSize: "11px", color: "#555", fontWeight: 600 }}>
                    Route line needs at least 2 stops.
                  </Typography>
                </Box>
              )}

              {selectedPopup && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: 10,
                    width: 240,
                    backgroundColor: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    p: 1,
                    zIndex: 11,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#333" }}>
                      {selectedPopup.customerName}
                    </Typography>
                    <IconButton size="small" onClick={() => setSelectedPopup(null)} sx={{ p: 0.2 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "#777" }}>Job ID: {selectedPopup.id}</Typography>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: getJobColor(selectedPopup.jobType) }}>
                    {selectedPopup.jobType}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#555" }}>{selectedPopup.volume}</Typography>
                  <Typography sx={{ fontSize: "12px", color: "#555" }}>{selectedPopup.time}</Typography>
                  <Typography sx={{ fontSize: "12px", color: "#555", mt: 0.5 }}>
                    {selectedPopup.vehicle
                      ? `${selectedPopup.vehicle} - ${selectedPopup.driver}`
                      : "Not assigned"}
                  </Typography>
                </Box>
              )}

              {!GOOGLE_MAPS_API_KEY && null}
            </Box>

            {/* ---- Route Planning Table ---- */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 1, color: "#201F1E" }}
            >
              Route Planning Table
            </Typography>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: "6px",
                mb: 1.5,
                maxHeight: 220,
                overflow: "auto",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {activeTab === "custom" && (
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "13px",
                          width: "5%",
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                    )}
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      Job
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      Task
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      Volume
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      Distance
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      Suggested Vehicle
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTab === "suggested" ? (
                    /* ---- Suggested mode ---- */
                    hasSuggested && suggestedJobs.length === 0 && apiRouteJobs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <Typography sx={{ fontSize: "13px", color: "#999" }}>
                            No combinations found. Try adjusting filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (apiRouteJobs.length > 0
                        ? apiRouteJobs
                        : suggestedJobs.length > 0
                          ? suggestedJobs
                          : filteredJobs
                      ).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell sx={{ fontSize: "12px", py: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: 12,
                                  color: getJobColor(row.jobType),
                                }}
                              />
                              {row.job}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1 }}>
                            {row.task}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1 }}>
                            {row.volume}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1 }}>
                            {row.time}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1 }}>
                            {row.distance}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", py: 1 }}>
                            {row.vehicle || "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  ) : (
                    /* ---- Custom mode – checkboxes ---- */
                    filteredJobs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography sx={{ fontSize: "13px", color: "#999" }}>
                            No jobs available for custom route.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredJobs.map((row) => {
                        const isSelected = customSelectedIds.includes(row.id);
                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              backgroundColor: isSelected
                                ? "rgba(0,150,136,0.06)"
                                : "inherit",
                            }}
                          >
                            <TableCell sx={{ py: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => handleCustomToggle(row.id)}
                                sx={{
                                  color: "#009688",
                                  "&.Mui-checked": { color: "#009688" },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: 12,
                                    color: getJobColor(row.jobType),
                                  }}
                                />
                                {row.job}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: 1 }}>
                              {row.task}
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: 1 }}>
                              {row.volume}
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: 1 }}>
                              {row.time}
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: 1 }}>
                              {row.distance}
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: 1 }}>
                              {row.vehicle || "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ---- Footer Summary ---- */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                py: 1,
                borderTop: "1px solid #e0e0e0",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: 600, color: "#555" }}
              >
                Total Volume:{" "}
                <span
                  style={{
                    color:
                      totalVolume > apiCapacityCuft
                        ? "#d32f2f"
                        : "#555",
                  }}
                >
                  {totalVolume} cuft
                </span>
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#999" }}
              >
                |
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: 600, color: "#555" }}
              >
                Total Miles:{" "}
                <span
                  style={{
                    color:
                      parseFloat(totalMiles) >
                      (parseFloat(filterData.radius) || 15)
                        ? "#d32f2f"
                        : "#555",
                  }}
                >
                  {totalMiles} mi
                </span>
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#999" }}
              >
                |
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: 600, color: "#555" }}
              >
                Est. Duration: {estDuration}
              </Typography>
              {allocatedLoad && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", color: "#999" }}
                  >
                    |
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", fontWeight: 600, color: "#555" }}
                  >
                    Allocated Load: {allocatedLoad} cuft
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RouteGoogleOptimiseModal;
